import Card from '@/components/feature/parking/card';
import ServiceGuide from '@/components/feature/parking/service-guide';
import StandardItem from './StandardItem';
import {
    PARKING_BRANCH_CONFIG,
    resolveParkingBranch,
    type ParkingBranchItem,
    type ParkingBranchSection,
} from '@/constants/parking/branch-parking-info';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

interface ParkingContentProps {
    storeCode?: string | null;
    isHorizontal?: boolean;
    showServiceGuide?: boolean;
}

function buildSectionAriaLabel(title: string, items: ParkingBranchItem[], t: TFunction): string {
    const itemLabels = items.map((item) => {
        if (item.type === 'text') return t(item.textKey);
        return `${t(item.labelKey)} ${t(item.valueKey)}`;
    });
    return [title, ...itemLabels].join(', ');
}

function renderSectionContent(items: ParkingBranchItem[], t: TFunction) {
    return items.map((item, index) => {
        if (item.type === 'text') {
            return (
                <p
                    key={`text-${index}`}
                    className="text-[var(--color-text-sub01)] text-[calc(20*var(--vh-unit))] widescreen:font-medium font-bold leading-[calc(26*var(--vh-unit))] tracking-[calc(-0.4*var(--vh-unit))]"
                >
                    {t(item.textKey)}
                </p>
            );
        }
        return (
            <StandardItem
                key={`${item.labelKey}-${index}`}
                label={t(item.labelKey)}
                value={t(item.valueKey)}
            />
        );
    });
}

function renderCard(section: ParkingBranchSection, t: TFunction) {
    const title = t(section.titleKey);
    const ariaLabel = buildSectionAriaLabel(title, section.items, t);

    return (
        <Card
            key={section.titleKey}
            ariaLabel={ariaLabel}
            title={title}
            content={
                <div className="space-y-[calc(8*var(--vh-unit))]">
                    {renderSectionContent(section.items, t)}
                </div>
            }
        />
    );
}

export default function ParkingContent({ storeCode, isHorizontal, showServiceGuide = true }: ParkingContentProps) {
    const { t } = useTranslation();
    const branch = resolveParkingBranch(storeCode);
    const sections = PARKING_BRANCH_CONFIG[branch];

    return (
        <>
            {showServiceGuide && <ServiceGuide />}
            <div
                className="widescreen:grid-cols-2 widescreen:gap-x-[calc(60*var(--vw-unit))] widescreen:gap-y-[calc(24*var(--vh-unit))] grid grid-cols-1 gap-[calc(60*var(--vw-unit))]"
                data-region-nav
                data-region-order={showServiceGuide ? 3 : 2}
            >
                {sections.map((section) => renderCard(section, t))}
            </div>
        </>
    );
}
